import React, { useCallback, useEffect, useState } from 'react';
import classes from '~/app/common/scss/section.module.scss';
import List from './List';
import Button from '~/app/common/Button';
import AddPartDialog from './Add';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import { getPartStoreKey, isPartsEditable } from './helper';
import Icon from '~/app/common/Icon';

export interface PartsProps {
  type: PartLinkTypes
}

export type PartsComponent = React.FC<PartsProps>;

const Parts: PartsComponent = ({ type }) => {

  const basePath = getPartStoreKey(type);
  const fetchPartsAction = useStoreActions(actions => actions[basePath].fetchParts);
  const setTempPartsAction = useStoreActions(actions => actions[basePath].setTempParts);
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const id = useStoreState(state => state[basePath].data.id);
  const status = useStoreState(state => state[basePath].data.status);
  const parts = useStoreState(state => state[basePath].data.parts ?? []);
  const [isOpenAddPartForm, setIsOpenAddPartForm] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(true);

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const addPartPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit = !netWorkStatus && isPartsEditable(type, status) && addPartPermission;

  const togglePartDialog = useCallback((state: boolean) => (): void => {
    setTempPartsAction(parts);
    setIsOpenAddPartForm(state);
  }, [parts, setTempPartsAction])

  useEffect(() => {
    if (!netWorkStatus) {
      fetchPartsAction(String(id));
    }
  }, [id, fetchPartsAction, netWorkStatus]);

  return (
    <>
      <div className={classes['container']}>
        <div className={classes['header']} onClick={() => setExpanded(!expanded)}>
          <span className={classes['headerText']}>{t('parts')} ({parts?.length ?? 0})</span>
          {canEdit && 
              <Button
                className={classes['addButton']}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePartDialog(true)();
                }}
              >
                {t('add')}
              </Button>
            }
          <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
        </div>
        {expanded && (
          parts.length === 0 ? (
            <p className={classes.empty}>{t('noParts')}</p>
          ) : (
            <List type={type}/>
          )
        )}
        {canEdit && <AddPartDialog open={isOpenAddPartForm} onClose={togglePartDialog(false)} type={type}/>}
        <hr className={classes['bottomLine']} />
      </div>
    </>

  );
};

export default Parts
